import { makeAutoObservable } from 'mobx';
import api from '../config/api';
import {
    EmailTemplate,
    EmailTemplateCreateRequest,
    EmailTemplateUpdateRequest,
    EmailStats
} from '../types';

export class EmailTemplateStore {
    templates: EmailTemplate[] = [];
    emailStats: EmailStats | null = null;
    loading = false;
    error: string | null = null;
    saving = false;

    constructor() {
        makeAutoObservable(this);
    }

    async fetchTemplates() {
        this.loading = true;
        this.error = null;

        try {
            const response = await api.get<EmailTemplate[]>('/api/email-templates');
            this.templates = response.data;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to fetch email templates';
            this.templates = [];
        } finally {
            this.loading = false;
        }
    }

    async createTemplate(templateData: EmailTemplateCreateRequest): Promise<boolean> {
        this.saving = true;
        this.error = null;

        try {
            const response = await api.post<EmailTemplate>('/api/email-templates', templateData);
            this.templates.push(response.data);
            return true;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to create email template';
            return false;
        } finally {
            this.saving = false;
        }
    }

    async updateTemplate(id: number, templateData: EmailTemplateUpdateRequest): Promise<boolean> {
        this.saving = true;
        this.error = null;

        try {
            const response = await api.put<EmailTemplate>(`/api/email-templates/${id}`, templateData);

            const index = this.templates.findIndex(t => t.id === id);
            if (index !== -1) {
                this.templates[index] = response.data;
            }

            return true;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to update email template';
            return false;
        } finally {
            this.saving = false;
        }
    }

    async deleteTemplate(id: number): Promise<boolean> {
        this.saving = true;
        this.error = null;

        try {
            await api.delete(`/api/email-templates/${id}`);
            this.templates = this.templates.filter(t => t.id !== id);
            return true;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to delete email template';
            return false;
        } finally {
            this.saving = false;
        }
    }

    async fetchEmailStats() {
        try {
            const response = await api.get<EmailStats>('/api/email-stats');
            this.emailStats = response.data;
        } catch (error: any) {
            console.error('Failed to fetch email stats:', error);
        }
    }

    async testEmailConnection(): Promise<boolean> {
        try {
            const response = await api.post('/api/test-email');
            return response.data.success;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to test email connection';
            return false;
        }
    }

    getTemplateById(id: number): EmailTemplate | undefined {
        return this.templates.find(t => t.id === id);
    }

    getTemplateByName(name: string): EmailTemplate | undefined {
        return this.templates.find(t => t.name === name);
    }

    get activeTemplates(): EmailTemplate[] {
        return this.templates.filter(t => t.is_active);
    }

    get inactiveTemplates(): EmailTemplate[] {
        return this.templates.filter(t => !t.is_active);
    }

    get systemTemplates(): EmailTemplate[] {
        return this.templates.filter(t => this.isSystemTemplate(t.name));
    }

    get customTemplates(): EmailTemplate[] {
        return this.templates.filter(t => !this.isSystemTemplate(t.name));
    }

    isSystemTemplate(name: string): boolean {
        const systemTemplateNames = [
            'email_verification',
            'application_processing',
            'application_reminder',
            'product_information'
        ];
        return systemTemplateNames.includes(name);
    }

    getTemplateDescription(name: string): string {
        const descriptions: { [key: string]: string } = {
            'email_verification': 'Отправляется для подтверждения email адреса',
            'application_processing': 'Отправляется когда заявка принята в обработку',
            'application_reminder': 'Напоминание о необходимости подтвердить email',
            'product_information': 'Информация о продукте для клиента'
        };
        return descriptions[name] || 'Пользовательский шаблон';
    }

    getAvailableVariables(name: string): string[] {
        const variables: { [key: string]: string[] } = {
            'email_verification': ['{{.FullName}}', '{{.VerificationURL}}', '{{.IsRetry}}'],
            'application_processing': ['{{.FullName}}', '{{.Company}}', '{{.CompanySize}}', '{{.Tariff}}', '{{.Phone}}'],
            'application_reminder': ['{{.FullName}}', '{{.VerificationURL}}'],
            'product_information': ['{{.FullName}}', '{{.Company}}', '{{.CompanySize}}', '{{.Tariff}}', '{{.Phone}}']
        };
        return variables[name] || ['{{.FullName}}', '{{.Company}}', '{{.Email}}', '{{.Phone}}', '{{.Tariff}}'];
    }

    get totalEmailsSent(): number {
        return this.emailStats?.sent_messages || 0;
    }

    get emailSuccessRate(): number {
        if (!this.emailStats || this.emailStats.total_messages === 0) return 0;
        return (this.emailStats.sent_messages / this.emailStats.total_messages) * 100;
    }

    resetError() {
        this.error = null;
    }

    clearTemplates() {
        this.templates = [];
    }

    clearStats() {
        this.emailStats = null;
    }
}